import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';

const ConveyancingMatterDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [matter, setMatter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [toast, setToast] = useState(null);
    const [saving, setSaving] = useState(false);

    // Modal states
    const [showPartyModal, setShowPartyModal] = useState(false);
    const [showDisbursementModal, setShowDisbursementModal] = useState(false);
    const [showEditMatterModal, setShowEditMatterModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // { type, id, name }
    const [editingParty, setEditingParty] = useState(null);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    // ESC key to close modals (HCI: User Control & Freedom)
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setShowPartyModal(false);
                setShowDisbursementModal(false);
                setShowEditMatterModal(false);
                setShowDeleteConfirm(null);
                setEditingParty(null);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    const getHeaders = () => {
        const token = localStorage.getItem('accessToken');
        return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    };

    const fetchMatter = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`/api/conveyancing/matters/${id}/`, { headers: getHeaders() });
            if (!response.ok) throw new Error('Failed to fetch matter');
            const data = await response.json();
            setMatter(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { fetchMatter(); }, [fetchMatter]);

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount || 0);
    };

    const handleStatusChange = async (newStatus) => {
        try {
            const response = await fetch(`/api/conveyancing/matters/${id}/update_status/`, {
                method: 'POST', headers: getHeaders(), body: JSON.stringify({ status: newStatus })
            });
            if (!response.ok) throw new Error('Failed to update status');
            showToast('Status updated successfully');
            fetchMatter();
        } catch (err) { showToast(err.message, 'error'); }
    };

    const handleToggleMilestone = async (milestoneId) => {
        try {
            const response = await fetch(`/api/conveyancing/milestones/${milestoneId}/toggle_complete/`, {
                method: 'POST', headers: getHeaders()
            });
            if (!response.ok) throw new Error('Failed to update milestone');
            fetchMatter();
        } catch (err) { showToast(err.message, 'error'); }
    };

    const handleDocStatusChange = async (docId, newStatus) => {
        try {
            const response = await fetch(`/api/conveyancing/documents/${docId}/update_status/`, {
                method: 'POST', headers: getHeaders(), body: JSON.stringify({ status: newStatus })
            });
            if (!response.ok) throw new Error('Failed to update document');
            fetchMatter();
        } catch (err) { showToast(err.message, 'error'); }
    };

    const handleMarkPaid = async (disbId) => {
        try {
            setSaving(true);
            const response = await fetch(`/api/conveyancing/disbursements/${disbId}/mark_paid/`, {
                method: 'POST', headers: getHeaders()
            });
            if (!response.ok) throw new Error('Failed');
            showToast('Marked as paid');
            fetchMatter();
        } catch (err) { showToast(err.message, 'error'); } finally { setSaving(false); }
    };

    // DELETE Matter (HCI: Confirmation before destructive action)
    const handleDeleteMatter = async () => {
        try {
            setSaving(true);
            const response = await fetch(`/api/conveyancing/matters/${id}/`, {
                method: 'DELETE', headers: getHeaders()
            });
            if (!response.ok) throw new Error('Failed to delete matter');
            showToast('Matter deleted successfully');
            setShowDeleteConfirm(null);
            navigate('/conveyancing/matters');
        } catch (err) { showToast(err.message, 'error'); } finally { setSaving(false); }
    };

    // EDIT Matter
    const [editMatterForm, setEditMatterForm] = useState({});
    const openEditMatter = () => {
        setEditMatterForm({
            title: matter.title || '',
            property_address: matter.property_address || '',
            property_description: matter.property_description || '',
            title_deed_number: matter.title_deed_number || '',
            erf_number: matter.erf_number || '',
            purchase_price: matter.purchase_price || '0.00',
            transfer_duty: matter.transfer_duty || '0.00',
            bond_amount: matter.bond_amount || '0.00',
            target_registration_date: matter.target_registration_date || '',
            notes: matter.notes || '',
        });
        setShowEditMatterModal(true);
    };
    const handleEditMatter = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const response = await fetch(`/api/conveyancing/matters/${id}/`, {
                method: 'PATCH', headers: getHeaders(), body: JSON.stringify(editMatterForm)
            });
            if (!response.ok) throw new Error('Failed to update matter');
            setShowEditMatterModal(false);
            showToast('Matter updated successfully');
            fetchMatter();
        } catch (err) { showToast(err.message, 'error'); } finally { setSaving(false); }
    };

    // DELETE Party
    const handleDeleteParty = async (partyId) => {
        try {
            setSaving(true);
            const response = await fetch(`/api/conveyancing/parties/${partyId}/`, {
                method: 'DELETE', headers: getHeaders()
            });
            if (!response.ok) throw new Error('Failed to delete party');
            setShowDeleteConfirm(null);
            showToast('Party removed');
            fetchMatter();
        } catch (err) { showToast(err.message, 'error'); } finally { setSaving(false); }
    };

    // EDIT Party
    const openEditParty = (party) => {
        setPartyForm({ role: party.role, name: party.name, id_number: party.id_number || '', email: party.email || '', phone: party.phone || '', address: party.address || '', notes: party.notes || '' });
        setEditingParty(party);
        setShowPartyModal(true);
    };
    const handleEditParty = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const response = await fetch(`/api/conveyancing/parties/${editingParty.id}/`, {
                method: 'PATCH', headers: getHeaders(), body: JSON.stringify(partyForm)
            });
            if (!response.ok) throw new Error('Failed to update party');
            setShowPartyModal(false);
            setEditingParty(null);
            setPartyForm({ role: 'BUYER', name: '', id_number: '', email: '', phone: '', address: '', notes: '' });
            showToast('Party updated');
            fetchMatter();
        } catch (err) { showToast(err.message, 'error'); } finally { setSaving(false); }
    };

    // DELETE Disbursement
    const handleDeleteDisbursement = async (disbId) => {
        try {
            setSaving(true);
            const response = await fetch(`/api/conveyancing/disbursements/${disbId}/`, {
                method: 'DELETE', headers: getHeaders()
            });
            if (!response.ok) throw new Error('Failed to delete disbursement');
            setShowDeleteConfirm(null);
            showToast('Disbursement deleted');
            fetchMatter();
        } catch (err) { showToast(err.message, 'error'); } finally { setSaving(false); }
    };

    // Party form
    const [partyForm, setPartyForm] = useState({ role: 'BUYER', name: '', id_number: '', email: '', phone: '', address: '', notes: '' });
    const handleAddParty = async (e) => {
        e.preventDefault();
        if (editingParty) return handleEditParty(e);
        try {
            setSaving(true);
            const response = await fetch('/api/conveyancing/parties/', {
                method: 'POST', headers: getHeaders(), body: JSON.stringify({ ...partyForm, matter: id })
            });
            if (!response.ok) throw new Error('Failed to add party');
            setShowPartyModal(false);
            setPartyForm({ role: 'BUYER', name: '', id_number: '', email: '', phone: '', address: '', notes: '' });
            showToast('Party added');
            fetchMatter();
        } catch (err) { showToast(err.message, 'error'); } finally { setSaving(false); }
    };

    // Disbursement form
    const [disbForm, setDisbForm] = useState({ disbursement_type: 'PAYMENT', category: 'ATTORNEYS_FEE', description: '', amount: '', date: new Date().toISOString().split('T')[0], reference: '', notes: '' });
    const handleAddDisbursement = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const response = await fetch('/api/conveyancing/disbursements/', {
                method: 'POST', headers: getHeaders(), body: JSON.stringify({ ...disbForm, matter: id })
            });
            if (!response.ok) throw new Error('Failed to add disbursement');
            setShowDisbursementModal(false);
            setDisbForm({ disbursement_type: 'PAYMENT', category: 'ATTORNEYS_FEE', description: '', amount: '', date: new Date().toISOString().split('T')[0], reference: '', notes: '' });
            showToast('Disbursement added');
            fetchMatter();
        } catch (err) { showToast(err.message, 'error'); } finally { setSaving(false); }
    };

    const statusColors = {
        'INSTRUCTION': 'bg-blue-100 text-blue-700', 'SEARCHES': 'bg-purple-100 text-purple-700',
        'DRAFTING': 'bg-indigo-100 text-indigo-700', 'SIGNING': 'bg-yellow-100 text-yellow-700',
        'LODGEMENT': 'bg-orange-100 text-orange-700', 'REGISTRATION': 'bg-cyan-100 text-cyan-700',
        'COMPLETED': 'bg-green-100 text-green-700', 'CANCELLED': 'bg-red-100 text-red-700',
        'ON_HOLD': 'bg-gray-100 text-gray-700',
    };

    const docStatusColors = {
        'REQUIRED': 'bg-red-100 text-red-700', 'REQUESTED': 'bg-yellow-100 text-yellow-700',
        'RECEIVED': 'bg-blue-100 text-blue-700', 'VERIFIED': 'bg-green-100 text-green-700',
        'NOT_APPLICABLE': 'bg-gray-100 text-gray-500',
    };

    const tabs = [
        { key: 'overview', label: 'Overview' },
        { key: 'parties', label: 'Parties' },
        { key: 'milestones', label: 'Progress' },
        { key: 'financials', label: 'Financials' },
        { key: 'documents', label: 'Documents' },
    ];

    if (loading) {
        return (
            <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
                <div className="h-4 w-64 bg-gray-200 rounded animate-pulse mb-4"></div>
                <div className="h-8 w-96 bg-gray-200 rounded animate-pulse mb-6"></div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100">
                    <div className="space-y-4">
                        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 bg-gray-100 rounded animate-pulse"></div>)}
                    </div>
                </div>
            </div>
        );
    }

    if (error || !matter) {
        return (
            <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
                <div className="max-w-md mx-auto mt-12 bg-white dark:bg-gray-800 rounded-2xl p-8 text-center">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Matter Not Found</h2>
                    <p className="text-gray-500 dark:text-gray-400 mb-6">{error || 'Unable to load this matter.'}</p>
                    <Link to="/conveyancing/matters" className="text-blue-600 hover:underline">Back to Matters</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
            {toast && (
                <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg text-white font-medium ${toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
                    {toast.message}
                </div>
            )}

            {/* Breadcrumb & Header */}
            <nav className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                <Link to="/conveyancing" className="hover:text-blue-600">Conveyancing</Link>
                <span className="mx-2">&rsaquo;</span>
                <Link to="/conveyancing/matters" className="hover:text-blue-600">Matters</Link>
                <span className="mx-2">&rsaquo;</span>
                <span>{matter.reference_number}</span>
            </nav>

            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{matter.title}</h1>
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusColors[matter.status] || 'bg-gray-100 text-gray-700'}`}>
                            {matter.status_display}
                        </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{matter.reference_number} &bull; {matter.transfer_type_display} &bull; {matter.property_type_display}</p>
                </div>
                <div className="mt-3 sm:mt-0 flex gap-2">
                    <button onClick={openEditMatter}
                        className="px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
                        aria-label="Edit matter details">
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        Edit
                    </button>
                    <button onClick={() => setShowDeleteConfirm({ type: 'matter', id, name: matter.title })}
                        className="px-3 py-2 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 font-medium transition-colors"
                        aria-label="Delete matter">
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        Delete
                    </button>
                    <select
                        value={matter.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500"
                        aria-label="Change matter status"
                    >
                        <option value="INSTRUCTION">Instruction</option>
                        <option value="SEARCHES">Searches</option>
                        <option value="DRAFTING">Drafting</option>
                        <option value="SIGNING">Signing</option>
                        <option value="LODGEMENT">Lodgement</option>
                        <option value="REGISTRATION">Registration</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="ON_HOLD">On Hold</option>
                        <option value="CANCELLED">Cancelled</option>
                    </select>
                </div>
            </div>

            {/* Progress bar */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Overall Progress</span>
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{matter.completion_percentage}%</span>
                </div>
                <div className="w-full h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full">
                    <div className="h-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all" style={{ width: `${matter.completion_percentage}%` }}></div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                <nav className="flex space-x-1 overflow-x-auto">
                    {tabs.map(tab => (
                        <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                                activeTab === tab.key
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'
                            }`}>
                            {tab.label}
                            {tab.key === 'parties' && matter.parties?.length > 0 && (
                                <span className="ml-1.5 text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded-full">{matter.parties.length}</span>
                            )}
                            {tab.key === 'documents' && matter.required_documents?.length > 0 && (
                                <span className="ml-1.5 text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded-full">{matter.required_documents.length}</span>
                            )}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Property Info */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Property Details</h3>
                        <dl className="space-y-3">
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Address</dt><dd className="text-sm text-gray-900 dark:text-white text-right max-w-[60%]">{matter.property_address}</dd></div>
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Description</dt><dd className="text-sm text-gray-900 dark:text-white text-right max-w-[60%]">{matter.property_description}</dd></div>
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Title Deed</dt><dd className="text-sm text-gray-900 dark:text-white">{matter.title_deed_number || '-'}</dd></div>
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Erf/Stand No.</dt><dd className="text-sm text-gray-900 dark:text-white">{matter.erf_number || '-'}</dd></div>
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Property Type</dt><dd className="text-sm text-gray-900 dark:text-white">{matter.property_type_display}</dd></div>
                        </dl>
                    </div>

                    {/* Financial Summary */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Financial Summary</h3>
                        <dl className="space-y-3">
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Purchase Price</dt><dd className="text-sm font-bold text-gray-900 dark:text-white">{formatCurrency(matter.purchase_price, matter.currency)}</dd></div>
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Transfer Duty</dt><dd className="text-sm text-gray-900 dark:text-white">{formatCurrency(matter.transfer_duty, matter.currency)}</dd></div>
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Bond Amount</dt><dd className="text-sm text-gray-900 dark:text-white">{formatCurrency(matter.bond_amount, matter.currency)}</dd></div>
                            <hr className="border-gray-100 dark:border-gray-700" />
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Total Received</dt><dd className="text-sm text-green-600 font-medium">{formatCurrency(matter.total_received, matter.currency)}</dd></div>
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Total Paid Out</dt><dd className="text-sm text-red-600 font-medium">{formatCurrency(matter.total_paid, matter.currency)}</dd></div>
                        </dl>
                        {matter.trust_account_name && (
                            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                <p className="text-sm text-blue-700 dark:text-blue-300">
                                    Linked to trust account: <Link to={`/trust/accounts/${matter.trust_account}`} className="font-medium underline">{matter.trust_account_name}</Link>
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Key Dates */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Key Dates</h3>
                        <dl className="space-y-3">
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Instruction Date</dt><dd className="text-sm text-gray-900 dark:text-white">{matter.instruction_date}</dd></div>
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Target Registration</dt><dd className="text-sm text-gray-900 dark:text-white">{matter.target_registration_date || 'Not set'}</dd></div>
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Actual Registration</dt><dd className="text-sm text-gray-900 dark:text-white">{matter.actual_registration_date || '-'}</dd></div>
                        </dl>
                    </div>

                    {/* Assignment */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Assignment</h3>
                        <dl className="space-y-3">
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Client</dt><dd className="text-sm text-gray-900 dark:text-white">{matter.client_name}</dd></div>
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Created By</dt><dd className="text-sm text-gray-900 dark:text-white">{matter.created_by_name || '-'}</dd></div>
                            <div className="flex justify-between"><dt className="text-sm text-gray-500 dark:text-gray-400">Assigned To</dt><dd className="text-sm text-gray-900 dark:text-white">{matter.assigned_to_name || 'Unassigned'}</dd></div>
                        </dl>
                        {matter.notes && (
                            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                <p className="text-sm text-gray-700 dark:text-gray-300">{matter.notes}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'parties' && (
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Parties</h3>
                        <button onClick={() => setShowPartyModal(true)}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            Add Party
                        </button>
                    </div>
                    {matter.parties?.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-8 text-center">
                            <p className="text-gray-500 dark:text-gray-400">No parties added yet</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {matter.parties.map(party => (
                                <div key={party.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">{party.role_display}</span>
                                            <h4 className="text-base font-semibold text-gray-900 dark:text-white mt-2">{party.name}</h4>
                                            {party.id_number && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">ID: {party.id_number}</p>}
                                        </div>
                                        <div className="flex gap-1">
                                            <button onClick={() => openEditParty(party)}
                                                className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                aria-label={`Edit ${party.name}`} title="Edit party">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                            </button>
                                            <button onClick={() => setShowDeleteConfirm({ type: 'party', id: party.id, name: party.name })}
                                                className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                aria-label={`Delete ${party.name}`} title="Remove party">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                        {party.email && <p>{party.email}</p>}
                                        {party.phone && <p>{party.phone}</p>}
                                        {party.address && <p className="text-xs">{party.address}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'milestones' && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Progress Milestones</h3>
                    <div className="space-y-3">
                        {matter.milestones?.map((milestone, idx) => (
                            <div key={milestone.id}
                                className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
                                    milestone.is_completed
                                        ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                                        : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-700'
                                }`}>
                                <button onClick={() => handleToggleMilestone(milestone.id)}
                                    className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                                        milestone.is_completed
                                            ? 'bg-green-500 border-green-500 text-white'
                                            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
                                    }`}>
                                    {milestone.is_completed && (
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </button>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <h4 className={`text-sm font-semibold ${milestone.is_completed ? 'text-green-700 dark:text-green-400 line-through' : 'text-gray-900 dark:text-white'}`}>
                                            {idx + 1}. {milestone.title}
                                        </h4>
                                        {milestone.due_date && (
                                            <span className="text-xs text-gray-400 dark:text-gray-500">{milestone.due_date}</span>
                                        )}
                                    </div>
                                    {milestone.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{milestone.description}</p>}
                                    {milestone.completed_date && <p className="text-xs text-green-600 dark:text-green-400 mt-1">Completed: {milestone.completed_date}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {activeTab === 'financials' && (
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Disbursements & Receipts</h3>
                        <button onClick={() => setShowDisbursementModal(true)}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            Add Entry
                        </button>
                    </div>

                    {/* Summary cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
                            <p className="text-xs font-medium text-green-600 dark:text-green-400 uppercase">Total Received</p>
                            <p className="text-xl font-bold text-green-700 dark:text-green-300 mt-1">{formatCurrency(matter.total_received, matter.currency)}</p>
                        </div>
                        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 border border-red-200 dark:border-red-800">
                            <p className="text-xs font-medium text-red-600 dark:text-red-400 uppercase">Total Paid</p>
                            <p className="text-xl font-bold text-red-700 dark:text-red-300 mt-1">{formatCurrency(matter.total_paid, matter.currency)}</p>
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                            <p className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase">Net Balance</p>
                            <p className="text-xl font-bold text-blue-700 dark:text-blue-300 mt-1">{formatCurrency((matter.total_received || 0) - (matter.total_paid || 0), matter.currency)}</p>
                        </div>
                    </div>

                    {matter.disbursements?.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-8 text-center">
                            <p className="text-gray-500 dark:text-gray-400">No disbursements recorded yet</p>
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</th>
                                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                                        <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
                                        <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                                        <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {matter.disbursements.map(d => (
                                        <tr key={d.id} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                            <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">{d.date}</td>
                                            <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">{d.category_display}</td>
                                            <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">{d.description}</td>
                                            <td className="py-3 px-4">
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${d.disbursement_type === 'RECEIPT' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                    {d.disbursement_type_display}
                                                </span>
                                            </td>
                                            <td className={`py-3 px-4 text-sm font-medium text-right ${d.disbursement_type === 'RECEIPT' ? 'text-green-600' : 'text-red-600'}`}>
                                                {formatCurrency(d.amount, matter.currency)}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                {d.is_paid ? (
                                                    <span className="text-xs font-medium text-green-600">Paid</span>
                                                ) : (
                                                    <button onClick={() => handleMarkPaid(d.id)} disabled={saving} className="text-xs text-blue-600 hover:underline disabled:opacity-50">Mark Paid</button>
                                                )}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <button onClick={() => setShowDeleteConfirm({ type: 'disbursement', id: d.id, name: d.description || d.category_display })}
                                                    className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                                                    aria-label={`Delete ${d.description || 'disbursement'}`} title="Delete entry">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'documents' && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Document Checklist</h3>
                    <div className="space-y-2">
                        {matter.required_documents?.map(doc => (
                            <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className={`w-2.5 h-2.5 rounded-full ${
                                        doc.status === 'VERIFIED' ? 'bg-green-500' :
                                        doc.status === 'RECEIVED' ? 'bg-blue-500' :
                                        doc.status === 'REQUESTED' ? 'bg-yellow-500' :
                                        doc.status === 'NOT_APPLICABLE' ? 'bg-gray-300' : 'bg-red-500'
                                    }`}></div>
                                    <span className="text-sm text-gray-900 dark:text-white">{doc.document_name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <select
                                        value={doc.status}
                                        onChange={(e) => handleDocStatusChange(doc.id, e.target.value)}
                                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${docStatusColors[doc.status] || 'bg-gray-100 text-gray-700'}`}
                                    >
                                        <option value="REQUIRED">Required</option>
                                        <option value="REQUESTED">Requested</option>
                                        <option value="RECEIVED">Received</option>
                                        <option value="VERIFIED">Verified</option>
                                        <option value="NOT_APPLICABLE">N/A</option>
                                    </select>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Add Party Modal */}
            {showPartyModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg mx-4">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{editingParty ? 'Edit Party' : 'Add Party'}</h2>
                            <button onClick={() => { setShowPartyModal(false); setEditingParty(null); setPartyForm({ role: 'BUYER', name: '', id_number: '', email: '', phone: '', address: '', notes: '' }); }} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handleAddParty} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role *</label>
                                    <select required value={partyForm.role} onChange={(e) => setPartyForm({ ...partyForm, role: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                                        <option value="BUYER">Buyer/Purchaser</option><option value="SELLER">Seller/Vendor</option>
                                        <option value="BONDHOLDER">Bond Holder/Bank</option><option value="AGENT">Estate Agent</option>
                                        <option value="CORRESPONDENT">Correspondent Attorney</option><option value="GUARANTOR">Guarantor</option>
                                        <option value="WITNESS">Witness</option><option value="OTHER">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                                    <input type="text" required value={partyForm.name} onChange={(e) => setPartyForm({ ...partyForm, name: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                                    <input type="email" value={partyForm.email} onChange={(e) => setPartyForm({ ...partyForm, email: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                                    <input type="text" value={partyForm.phone} onChange={(e) => setPartyForm({ ...partyForm, phone: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ID/Registration No.</label>
                                <input type="text" value={partyForm.id_number} onChange={(e) => setPartyForm({ ...partyForm, id_number: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => { setShowPartyModal(false); setEditingParty(null); setPartyForm({ role: 'BUYER', name: '', id_number: '', email: '', phone: '', address: '', notes: '' }); }} className="px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 text-sm">Cancel</button>
                                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                                    {saving ? 'Saving...' : editingParty ? 'Update Party' : 'Add Party'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Disbursement Modal */}
            {showDisbursementModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg mx-4">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Add Disbursement / Receipt</h2>
                            <button onClick={() => setShowDisbursementModal(false)} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handleAddDisbursement} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type *</label>
                                    <select required value={disbForm.disbursement_type} onChange={(e) => setDisbForm({ ...disbForm, disbursement_type: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                                        <option value="RECEIPT">Receipt (Funds In)</option>
                                        <option value="PAYMENT">Payment (Funds Out)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category *</label>
                                    <select required value={disbForm.category} onChange={(e) => setDisbForm({ ...disbForm, category: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                                        <option value="DEPOSIT">Purchase Deposit</option><option value="TRANSFER_DUTY">Transfer Duty</option>
                                        <option value="RATES_CLEARANCE">Rates Clearance</option><option value="LEVY_CLEARANCE">Levy Clearance</option>
                                        <option value="COMPLIANCE">Compliance Certificate</option><option value="DEEDS_OFFICE">Deeds Office Fees</option>
                                        <option value="POSTAGE">Postage & Petties</option><option value="BOND_COSTS">Bond Registration Costs</option>
                                        <option value="CANCELLATION">Bond Cancellation Costs</option><option value="ATTORNEYS_FEE">Attorney's Fee</option>
                                        <option value="VAT">VAT</option><option value="PURCHASE_PRICE">Purchase Price Balance</option>
                                        <option value="AGENT_COMMISSION">Estate Agent Commission</option><option value="OTHER">Other</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description *</label>
                                <input type="text" required value={disbForm.description} onChange={(e) => setDisbForm({ ...disbForm, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount *</label>
                                    <input type="number" step="0.01" min="0.01" required value={disbForm.amount}
                                        onChange={(e) => setDisbForm({ ...disbForm, amount: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date *</label>
                                    <input type="date" required value={disbForm.date} onChange={(e) => setDisbForm({ ...disbForm, date: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reference</label>
                                <input type="text" value={disbForm.reference} onChange={(e) => setDisbForm({ ...disbForm, reference: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShowDisbursementModal(false)} className="px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 text-sm">Cancel</button>
                                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                                    {saving ? 'Saving...' : 'Add Entry'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Matter Modal */}
            {showEditMatterModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center pt-8 overflow-y-auto" role="dialog" aria-modal="true" aria-label="Edit matter">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl mx-4 mb-8">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Edit Matter Details</h2>
                            <button onClick={() => setShowEditMatterModal(false)} className="text-gray-400 hover:text-gray-600">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handleEditMatter} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Matter Title</label>
                                <input type="text" value={editMatterForm.title} onChange={(e) => setEditMatterForm({ ...editMatterForm, title: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Property Address</label>
                                <textarea value={editMatterForm.property_address} onChange={(e) => setEditMatterForm({ ...editMatterForm, property_address: e.target.value })}
                                    rows={2} className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Property Description</label>
                                <textarea value={editMatterForm.property_description} onChange={(e) => setEditMatterForm({ ...editMatterForm, property_description: e.target.value })}
                                    rows={2} className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title Deed Number</label>
                                    <input type="text" value={editMatterForm.title_deed_number} onChange={(e) => setEditMatterForm({ ...editMatterForm, title_deed_number: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Erf/Stand Number</label>
                                    <input type="text" value={editMatterForm.erf_number} onChange={(e) => setEditMatterForm({ ...editMatterForm, erf_number: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Purchase Price</label>
                                    <input type="number" step="0.01" min="0" value={editMatterForm.purchase_price}
                                        onChange={(e) => setEditMatterForm({ ...editMatterForm, purchase_price: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Transfer Duty</label>
                                    <input type="number" step="0.01" min="0" value={editMatterForm.transfer_duty}
                                        onChange={(e) => setEditMatterForm({ ...editMatterForm, transfer_duty: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bond Amount</label>
                                    <input type="number" step="0.01" min="0" value={editMatterForm.bond_amount}
                                        onChange={(e) => setEditMatterForm({ ...editMatterForm, bond_amount: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Registration Date</label>
                                <input type="date" value={editMatterForm.target_registration_date}
                                    onChange={(e) => setEditMatterForm({ ...editMatterForm, target_registration_date: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                                <textarea value={editMatterForm.notes} onChange={(e) => setEditMatterForm({ ...editMatterForm, notes: e.target.value })}
                                    rows={2} className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShowEditMatterModal(false)} className="px-5 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium">Cancel</button>
                                <button type="submit" disabled={saving} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog (HCI: Error Prevention) */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" role="alertdialog" aria-modal="true" aria-label="Confirm deletion">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Confirm Delete</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Are you sure you want to delete <strong>{showDeleteConfirm.name}</strong>? This action cannot be undone.
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowDeleteConfirm(null)} className="px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 text-sm font-medium">Cancel</button>
                            <button
                                onClick={() => {
                                    if (showDeleteConfirm.type === 'matter') handleDeleteMatter();
                                    else if (showDeleteConfirm.type === 'party') handleDeleteParty(showDeleteConfirm.id);
                                    else if (showDeleteConfirm.type === 'disbursement') handleDeleteDisbursement(showDeleteConfirm.id);
                                }}
                                disabled={saving}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                                {saving ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConveyancingMatterDetail;
